<template>
  <div class="markers">
    <template
      v-for="markerObject in markersWithMergedMarkers"
      v-bind:key="markerObject.marker.id"
    >
      <div
        class="marker"
        :class="[
          { 'marker marker--selected': markerObject.marker.showPopupContent },
          { 'marker--is-colliding': markerObject.collisionInfo },
          { 'marker--disabled': isMarkerPartOfGroup(markerObject.marker) },
        ]"
        :style="calcPositionStyles(markerObject)"
      >
        <template v-if="markerObject.marker.type === 'case-group'">
          <div
            class="marker__icon"
            :class="[
              'marker__icon--case-group',
              'marker__icon--' + markerObject.marker.colorTheme,
            ]"
            @click="openCaseGroup(markerObject)"
          >
            <div class="marker__case-group-count">
              {{ markerObject.margedCasesCount }}
            </div>
          </div>

          <div
            v-if="markerObject.marker.showPopupContent"
            class="marker__case-marker-popup case-marker-popup"
            :class="[
              'case-marker-popup--' + markerObject.marker.colorTheme,
              {
                'case-marker-popup--upward': popupShouldDrawUpward(
                  markerObject.marker
                ),
              },
            ]"
          >
            <div
              v-for="marker in markerObject.markers"
              :key="marker.id"
              class="case-marker-popup__frame"
            >
              <div class="case-marker-popup__name">{{ marker.popupName }}</div>
              <div class="case-marker-popup__location">
                {{ marker.popupLocation }}
              </div>

              <router-link
                v-if="marker.popupLinkRouterTo"
                router-link
                class="case-marker-popup__link"
                :to="marker.popupLinkRouterTo"
                >{{ marker.popupLinkText }}</router-link
              >
              <div
                v-else-if="marker.popupLinkCallback"
                class="case-marker-popup__link"
                @click="marker.popupLinkCallback"
              >
                {{ marker.popupLinkText }}
              </div>
            </div>
          </div>
        </template>

        <template v-else>
          <div
            class="marker__icon"
            :data-marker-id="markerObject.marker.id"
            :class="[
              'marker__icon--' + markerObject.marker.type,
              'marker__icon--' + markerObject.marker.colorTheme,
            ]"
            @click="handleMarkerClick(markerObject.marker)"
          >
            <template
              v-if="markerObject.marker.id === selectedMarker"
            >
              <div class="marker_close_icon" v-if="markerObject.marker.type !== 'datalayer'">X</div>
            </template>
            <template v-else>
              <svg
                class="marker_svg"
                id="Icon_feather-plus"
                data-name="Icon feather-plus"
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                 @click.stop="handleMarkerClick(markerObject.marker)"
              >
                <path
                  class="marker_icon_svg"
                  id="Path_1704"
                  data-name="Path 1704"
                  d="M18,7.5v12"
                  transform="translate(-12 -7.5)"
                />
                <path
                  class="marker_icon_svg"
                  id="Path_1705"
                  data-name="Path 1705"
                  d="M7.5,18h12"
                  transform="translate(-7.5 -12)"
                />
              </svg>
            </template>
            <div
              v-if="markerObject.marker.type === 'datalayer'"
              class="marker__datalayer-icon"
            ></div>
          </div>

          <div
            v-if="markerObject.marker.showPopupContent && !isEuropeUrl"
            class="marker__case-marker-popup case-marker-popup"
            :class="'case-marker-popup--' + markerObject.marker.colorTheme"
          >
            <div class="case-marker-popup__frame">
              <div class="case-marker-popup__name">
                {{ markerObject.marker.popupName }}
              </div>
              <div class="case-marker-popup__location">
                {{ markerObject.marker.popupLocation }}
              </div>

              <router-link
                v-if="markerObject.marker.popupLinkRouterTo"
                router-link
                class="case-marker-popup__link"
                :to="markerObject.marker.popupLinkRouterTo"
                >{{ markerObject.marker.popupLinkText }}</router-link
              >
              <div
                v-else-if="markerObject.marker.popupLinkCallback"
                class="case-marker-popup__link"
                @click="markerObject.marker.popupLinkCallback"
              >
                {{ markerObject.marker.popupLinkText }}
              </div>
            </div>
          </div>
          <div
            v-else-if="markerObject.marker.showPopupContent"
            class="marker__case-marker-popup case-marker-popup"
            :class="'case-marker-popup--' + markerObject.marker.colorTheme"
            :style="popupLocationStyles"
          >
            <div
              :ref="'euCaseContainer-' + markerObject.marker.id"
              class="eu-case-marker-popup__frame eu-case"
            >
              <img
                v-show="markerObject.marker.euCaseData?.topImage?.src"
                class="eu-case-img"
                :src="getMediaUrl(markerObject.marker.euCaseData.topImage.src)"
              />
              <p class="eu-image_location">
                {{ markerObject.marker.euCaseData.locationOfImage }}
              </p>
              <h1 class="eu-case_title">
                {{ markerObject.marker.euCaseData.heading.toUpperCase() }}
              </h1>
              <article class="eu-case-article">
                <p v-if="markerObject.marker.euCaseData.caseText">
                  {{ markerObject.marker.euCaseData.caseText }}
                </p>
              </article>
              <div
                v-if="markerObject.marker.euCaseData.keyArea.length >= 1"
                class="key-area"
              >
                <p class="key-area-title">KEY AREAS OF COOPERATION</p>
                <div class="key-item-container">
                  <div
                    v-for="keyArea in markerObject.marker.euCaseData.keyArea"
                    :key="keyArea.id"
                    class="key-area__item"
                  >
                    <img :src="getMediaUrl(keyArea.areaIcon.src)" />
                    <p>{{ keyArea.areaTitle }}</p>
                  </div>
                </div>
              </div>
              <div>
                <div
                  class="case_link"
                  v-if="markerObject.marker.euCaseData.link"
                  @click="
                    openIframeOverlay(markerObject.marker.euCaseData.link)
                  "
                >
                  {{ markerObject.marker.euCaseData.linkText }}
                  <svg
                    class="case_link_icon"
                    xmlns="http://www.w3.org/2000/svg"
                    width="9.33"
                    height="15.109"
                    viewBox="0 0 9.33 15.109"
                  >
                    <path
                      class="case_link_icon"
                      d="M1.775,0,7.555,5.767,13.334,0l1.775,1.775L7.555,9.33,0,1.775Z"
                      transform="translate(0 15.109) rotate(-90)"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
    <div v-if="showIframeOverlay" class="iframe-overlay">
      <button class="iframe-close-button" @click="closeIframeOverlay">X</button>
      <iframe :src="iframeUrl" frameborder="0" class="iframe-content"></iframe>
    </div>
  </div>
</template>

<script>
import config from "@/config.js";

export default {
  name: "Markers",
  props: {
    markers: Array,
    markerMergeRadius: Number, // NOTE: Sets the colission radius
    pointAtWhichPopupsShouldDrawUpward: Number,
  },

  data() {
    return {
      selectedCaseGroupMarkerId: null,
      idsOfMarkersPlacedInGroups: {},
      euCaseHeight: 0,
      showIframeOverlay: false,
      iframeUrl: "",
      popupLocationStyles: {
        top: "0px",
      },
      selectedMarker: null,
    };
  },
  mounted() {
    document.addEventListener("click", this.handleGlobalClick);
    
   // Preload main marker images
  this.markers.forEach(marker => {
    const imgSrc = marker.euCaseData?.topImage?.src;
    if (imgSrc) {
      const img = new window.Image();
      img.src = this.getMediaUrl(imgSrc);
    }

    // Preload key area icons
    const keyAreas = marker.euCaseData?.keyArea || [];
    keyAreas.forEach(keyArea => {
      const iconSrc = keyArea.areaIcon?.src;
      if (iconSrc) {
        const img = new window.Image();
        img.src = this.getMediaUrl(iconSrc);
      }
    });
  });
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleGlobalClick);
  },
  watch: {
    "markerObject.marker.showPopupContent"(newValue) {
      if (!newValue) {
        this.popupLocationStyles = { top: "0px" };
      }
    },
    markers: {
      handler() {
        this.resetIdsOfMarkersPlacedInGroups();
      },
      immediate: true,
    },
  },
  computed: {
    isEuropeUrl() {
      return window.location.href.includes("europe");
    },
    isDenmarkUrl() {
    return window.location.href.includes("denmark");
  },
    collidingMarkers() {
      var result = [];

      const colissionRadius = this.markerMergeRadius;

      // NOTE: Grab all visible markers (while omiting datalayer markers)
      var visibleMarkers = [];
      this.markers.forEach((marker) => {
        if (marker.type === "case" && marker.positionInfo.opacity !== 0) {
          visibleMarkers.push(marker);
        }
      });

      visibleMarkers.forEach((marker) => {
        var otherMarkersCollidedWith = [];

        visibleMarkers.forEach((otherMarker) => {
          if (marker !== otherMarker) {
            // NOTE: Test for collision and add to set
            var distance = this.calcDistance(
              marker.positionInfo.x,
              marker.positionInfo.y,
              otherMarker.positionInfo.x,
              otherMarker.positionInfo.y
            );
            if (distance < colissionRadius) {
              otherMarkersCollidedWith.push(otherMarker);
            }
          }
        });

        if (otherMarkersCollidedWith.length) {
          result.push({
            marker: marker,
            otherMarkersCollidedWith: otherMarkersCollidedWith,
          });
        }
      });

      return result;
    },
   markersWithMergedMarkers() {
    if (this.isEuropeUrl) {
      return this.markers.map((marker) => ({
        type: "single-marker",
        marker: marker,
        collisionInfo: false,
        position: { x: marker.positionInfo.x, y: marker.positionInfo.y },
      }));
    }

    // Prepare
    const colissionRadius = this.markerMergeRadius;
    const visibleMarkers = this.markers.filter(
      (marker) => marker.type === "case" && marker.positionInfo.opacity !== 0
    );
    const visited = new Set();
    const mergedMarkers = [];
    const singleMarkers = [];

    // Helper to find all connected markers (cluster) using DFS
    function findCluster(startMarker, allMarkers, radius, visitedSet) {
      const cluster = [];
      const stack = [startMarker];
      visitedSet.add(startMarker.id);

      while (stack.length) {
        const marker = stack.pop();
        cluster.push(marker);

        allMarkers.forEach((otherMarker) => {
          if (
            !visitedSet.has(otherMarker.id) &&
            marker !== otherMarker
          ) {
            const dx = marker.positionInfo.x - otherMarker.positionInfo.x;
            const dy = marker.positionInfo.y - otherMarker.positionInfo.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < radius) {
              visitedSet.add(otherMarker.id);
              stack.push(otherMarker);
            }
          }
        });
      }
      return cluster;
    }

    // Main grouping logic
    visibleMarkers.forEach((marker) => {
      if (!visited.has(marker.id)) {
        const cluster = findCluster(marker, visibleMarkers, colissionRadius, visited);
        if (cluster.length > 1) {
          // Grouped marker
          let groupedMarkerId = "";
          let totalX = 0;
          let totalY = 0;
          cluster.forEach((m) => {
            totalX += m.positionInfo.x;
            totalY += m.positionInfo.y;
            groupedMarkerId += m.id;
          });
          const averagePosition = {
            x: totalX / cluster.length,
            y: totalY / cluster.length,
          };
          mergedMarkers.push({
            type: "merged-marker",
            margedCasesCount: cluster.length,
            marker: {
              id: groupedMarkerId,
              showPopupContent: this.selectedCaseGroupMarkerId === groupedMarkerId,
              type: "case-group",
              colorTheme: marker.colorTheme,
              positionInfo: {
                x: averagePosition.x,
                y: averagePosition.y,
                opacity: 1,
                pointerEvents: null,
              },
            },
            markers: cluster,
            position: { x: averagePosition.x, y: averagePosition.y },
          });
        } else {
          // Single marker
          singleMarkers.push({
            type: "single-marker",
            marker: marker,
            collisionInfo: false,
            position: { x: marker.positionInfo.x, y: marker.positionInfo.y },
          });
        }
      }
    });

    // Add datalayer and other non-case markers as singles
    this.markers.forEach((marker) => {
      if (
        marker.type !== "case" &&
        marker.positionInfo.opacity !== 0
      ) {
        singleMarkers.push({
          type: "single-marker",
          marker: marker,
          collisionInfo: false,
          position: { x: marker.positionInfo.x, y: marker.positionInfo.y },
        });
      }
    });

    return singleMarkers.concat(mergedMarkers);
  },
  },
  methods: {
    handleGlobalClick(event) {
    // Check if the click is outside of any marker
    const markerElements = document.querySelectorAll(".marker");
    let clickedInsideMarker = false;

    markerElements.forEach((markerElement) => {
      if (markerElement.contains(event.target)) {
        clickedInsideMarker = true;
      }
    });

    // If clicked outside and a marker or group is selected, deselect it
    if (!clickedInsideMarker) {
      if (this.selectedMarker !== null) {
        const selectedMarkerObject = this.markers.find(
          (marker) => marker.id === this.selectedMarker
        );

        if (selectedMarkerObject) {
          selectedMarkerObject.showPopupContent = false; // Close the popup
        }

        this.selectedMarker = null;
      }

      if (this.selectedCaseGroupMarkerId !== null) {
        this.selectedCaseGroupMarkerId = null; // Close the group marker
      }
    }
  },
    openIframeOverlay(url) {
      // Ensure the URL is absolute
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        url = "https://" + url; // Prepend https:// if missing
      }
      this.iframeUrl = url;
      this.showIframeOverlay = true;
    },
    closeIframeOverlay() {
      this.showIframeOverlay = false;
      this.iframeUrl = "";
    },
    handlePopupRender(markerId) {
      // Reset styles and height before recalculating
      this.popupLocationStyles = { top: "-200px", left: "0px" };
      this.euCaseHeight = 0;

      const position = this.getMarkerScreenPosition(markerId);

      // Wait for the DOM to update
      this.$nextTick(() => {
        const euCaseContainer = this.$refs["euCaseContainer-" + markerId];

        if (euCaseContainer) {
          // Delay the height calculation to allow images to load
          const containerHeight = euCaseContainer[0]?.clientHeight || 700; // Get the height of the container

          this.euCaseHeight = containerHeight; // Store the height if needed
           

          console.log("Marker position:", position);
          console.log("Container height:", this.euCaseHeight);
          if (position) {
            const containerHeight = window.innerHeight; // Get the height of the viewport or container
            const adjustment = containerHeight - position.y; // Adjust this value as needed
            const newLocation = adjustment - this.euCaseHeight;
            

            console.log("Adjustment:", adjustment, "New location:", newLocation);
            if (newLocation < 0) {
              console.log("Popup should draw upward");
              this.popupLocationStyles.top = newLocation - 180 + "px"; // Set the new top position
            }
            if (position.x > 900) {
              console.log("Popup should draw leftward");
              this.popupLocationStyles.left = -390 + "px"; // Set the new left position
            } else {
              console.log("Popup should draw rightward");
              this.popupLocationStyles.left = 40 + "px"; // Set the new left position
            }
          }
        } else {
          console.warn(`Container for marker ${markerId} not found.`);
        }
      });
    },
    getMarkerScreenPosition(markerId) {
      // Find the marker element by its ID
      const markerElement = document.querySelector(
        `[data-marker-id="${markerId}"]`
      );
      if (markerElement) {
        // Get the bounding rectangle of the marker
        const rect = markerElement.getBoundingClientRect();
        return {
          x: rect.left + window.scrollX, // X position relative to the document
          y: rect.top + window.scrollY, // Y position relative to the document
          width: rect.width,
          height: rect.height,
        };
      } else {
        console.warn(`Marker with ID ${markerId} not found.`);
        return null;
      }
    },

    resetIdsOfMarkersPlacedInGroups() {
      this.idsOfMarkersPlacedInGroups = {};
    },
    popupShouldDrawUpward(marker) {
      return marker.positionInfo.y > this.pointAtWhichPopupsShouldDrawUpward;
    },
    setMarkerPartOfGroup(marker) {
      this.idsOfMarkersPlacedInGroups[marker.id] = true;
    },
    isMarkerPartOfGroup(marker) {
      return this.idsOfMarkersPlacedInGroups[marker.id] !== undefined;
    },
    handleMarkerClick(marker) {
    // Close any previously selected marker


    if (this.selectedMarker !== null) {
      const previouslySelectedMarker = this.markers.find(
        (m) => m.id === this.selectedMarker
      );
      if (previouslySelectedMarker) {
        previouslySelectedMarker.showPopupContent = false;
      }
    }

    // Open the clicked marker
    if (this.selectedMarker === marker.id) {
      // If the marker is already selected, deselect it
      this.selectedMarker = null;
      marker.showPopupContent = false;
    } else {
      // Otherwise, select the marker and show its popup
      this.selectedMarker = marker.id;
      marker.showPopupContent = true;
    }
    if(!this.isDenmarkUrl && !this.isEuropeUrl) {
      this.$emit("markerClicked", marker);
    }
    // Render the popup after the DOM updates

    this.$nextTick(() => {
      this.handlePopupRender(marker.id);
    });
  },
    openMarker(marker) {
      if (this.selectedMarker === marker.id) {
        // If the marker is already selected, deselect it
        this.selectedMarker = null;
      } else {
        // Otherwise, select the marker
        this.selectedMarker = marker.id;
      }

      this.$emit("markerClicked", marker);
      this.selectedCaseGroupMarkerId = null;
    },
    openCaseGroup(markerObject) {
      // NOTE: Tell the parent component to close the markers they are responsible for.
      this.$emit("markerClicked", null);

      if (this.selectedCaseGroupMarkerId != markerObject.marker.id) {
        this.selectedCaseGroupMarkerId = markerObject.marker.id;
      } else {
        this.selectedCaseGroupMarkerId = null;
      }
    },
    calcDistance(x1, y1, x2, y2) {
      return Math.sqrt(Math.pow(x1 - x2, 2) + Math.pow(y1 - y2, 2));
    },
    markerIsColliding(marker) {
      var result = false;
      this.collidingMarkers.forEach((collisionInfo) => {
        if (collisionInfo.marker === marker) result = collisionInfo;
      });
      return result;
    },
    getMediaUrl(mediaItem) {
      var url = config.umbracoMediaPath + mediaItem;
      return url;
    },
    calcPositionStyles(markerObject) {
      var newPositionStyles = {
        x: markerObject.position.x,
        y: markerObject.position.y,
        opacity: markerObject.marker.positionInfo.opacity,
        pointerEvents: markerObject.marker.positionInfo.pointerEvents,
      };

      return (
        "transform:translate(" +
        newPositionStyles.x +
        "px," +
        newPositionStyles.y +
        "px);opacity:" +
        newPositionStyles.opacity +
        ";" +
        newPositionStyles.pointerEvents
      );
    },
  },
};
</script>
